import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Badge,
    Spinner,
    Alert,
    Modal,
} from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { cameFromState } from '@/features/applications/faculty/backLink';
import courseService from '@/shared/services/courseService';
import positionService from '@/shared/services/positionService';
import CourseTypeahead from '@/components/common/CourseTypeahead';
import CourseDetailModal, { creditsText } from './CourseDetailModal';
import './CoursesPage.css';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import ApplicationDeadlineCard from '@/components/common/ApplicationDeadlineCard';
import { StudentApplicationsNotOpen } from '@/components/common/ProgramPageLinks';
import { useTerm } from '@/shared/contexts/TermContext';
import {
    getCoordinatorRole,
    getCoordinatorProgram,
} from '@/shared/utils/coordinatorRole';
import { badgeTextFor } from '@/shared/utils/badgeText';

// The department list is whatever the imported catalog contains, not a fixed
// set: the 2026-2027 import carries 120 prefixes and which ones exist changes
// with each import.
const ALL_DEPARTMENTS = 'All';

// Fetched a page ahead of the sentinel reaching the viewport, so the next
// cards are usually already there by the time the reader gets to them.
const INFINITE_SCROLL_MARGIN = '300px';

const NO_POSITION_KEYS = new Set();

const positionKey = (subject, courseNumber) => `${subject}|${courseNumber}`;

const COORDINATOR_PROGRAM_NAMES = {
    si_coordinator: 'Supplemental Instruction (SI)',
    ta_coordinator: 'Teaching Assistant (TA)',
    la_coordinator: 'Learning Assistant (LA)',
};

function CoursesPage() {
    const location = useLocation();
    const { userProfile, effectiveRoles } = useAuth();
    const roles = resolveRoles({ effectiveRoles, userProfile });
    // False until /api/features answers, so the button never flashes in and
    // then out for a feature that is off.
    const applyEnabled = useFeature('studentApplication');
    // With hiring on, Apply sends a student to the positions open for the
    // course instead of to the application form.
    const hiringEnabled = useFeature('hiring');
    // The program dashboards the coordinator copy points to exist only with
    // this flag on.
    const coordinatorReviewEnabled = useFeature('coordinatorReview');
    const { currentTerm, currentTermCode, termsLoading } = useTerm();
    const isFaculty = roles.some((r) => ['faculty', 'admin'].includes(r));
    const isAdmin = Boolean(roles.includes('admin'));
    // Shared fixed si/ta/la scan order (client/src/shared/utils/
    // coordinatorRole.js), matching the server's (server/src/api/course/
    // course.controller.js) — a user holding more than one coordinator
    // role at once must resolve to the same program everywhere, or the
    // checkbox shown here, what ApplicationDetailPage.js shows, and the
    // program the server actually merges could all disagree.
    const coordinatorRole = getCoordinatorRole(roles);
    const coordinatorProgram = getCoordinatorProgram(roles);
    const readsPositions =
        applyEnabled && hiringEnabled && !isFaculty && !coordinatorRole;
    const [openPositionKeys, setOpenPositionKeys] = useState(NO_POSITION_KEYS);

    useEffect(() => {
        setOpenPositionKeys(NO_POSITION_KEYS);
        if (!readsPositions || !currentTermCode) return undefined;
        let cancelled = false;
        positionService
            .listPositions({ program: 'la', term: currentTermCode })
            .then((data) => {
                if (cancelled) return;
                setOpenPositionKeys(
                    new Set(
                        (data.positions || [])
                            .filter((p) => p.openSlots > 0)
                            .map((p) => positionKey(p.subject, p.courseNumber))
                    )
                );
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [readsPositions, currentTermCode]);
    // Browse is read-only for the people it exists for. A faculty member is
    // here to find their own course and start a request, and a catalog entry
    // holds no section details: one entry stands for several sections taught
    // by different instructors, so a CRN, section and meeting times belong to
    // the application. Configure is for program coordinators setting
    // eligibility, and an admin who coordinates a program.
    const canConfigure = Boolean(coordinatorRole);
    // Which programs a course is eligible for is not shown to one audience: a
    // faculty member browsing. Read across the whole catalog it tells them
    // which of their colleagues' courses are in play for support and for
    // which program. A student choosing where to apply needs exactly this
    // signal, and a coordinator or admin is who sets it, so both keep it.
    //
    // This is what the screen shows, not what the caller may have. The API
    // still returns the field to anyone; withholding it server-side is #382.
    // Do not read this flag as an access decision.
    const hidesEligibility = isFaculty && !canConfigure && !isAdmin;
    const [courses, setCourses] = useState([]);
    // How many courses match the current filters, which is not how many are
    // loaded. Infinite scroll stops when those two meet.
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    // Kept apart from `error`, which hides the grid, the result count and the
    // Load more button because on the first load there is nothing to hide.
    // A page that fails partway through has a list worth keeping: reusing
    // `error` would blank every course already loaded and remove the retry
    // control at the same moment the reader needs it.
    const [loadMoreError, setLoadMoreError] = useState(null);
    const [subjectPrefixes, setSubjectPrefixes] = useState([]);

    // Filters. The browser does not hold the catalog, so each of these is a
    // query parameter rather than a predicate over a loaded list.
    // Settled text from the search box, which owns the typing and the
    // debounce; this page only decides what the text does, which here is
    // narrow the grid.
    const [searchQuery, setSearchQuery] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState(ALL_DEPARTMENTS);
    // Clear empties the box by remounting it. The box keeps its own text, so
    // resetting the page's copy alone would leave the old words on screen
    // over an unfiltered grid.
    const [searchBoxKey, setSearchBoxKey] = useState(0);
    // Asks for the first page again without any filter changing. A failed
    // first page empties the list and its total, so there is no Load more to
    // press, and Clear re-runs nothing when the filters were already empty:
    // without this the only way past a transient failure is a browser reload.
    const [firstPageAttempt, setFirstPageAttempt] = useState(0);

    // Configure-course modal state (coordinators only)
    const [showConfigureModal, setShowConfigureModal] = useState(false);
    // The course whose details are open, or null when none is.
    const [detailCourse, setDetailCourse] = useState(null);
    // Configure opened from the pop-out: the pop-out's button is gone by the
    // time Configure's dialog records where focus came from, so it would
    // return focus to the top of the page. It goes back to the course's card
    // title instead.
    const configureReturnFocusRef = useRef(null);
    const [configuringCourse, setConfiguringCourse] = useState(null);
    const [configureForm, setConfigureForm] = useState({ eligible: false });
    const [submittingConfig, setSubmittingConfig] = useState(false);
    // Rendered inside the modal itself (not the page-level `error` banner,
    // which sits behind the modal's backdrop and is invisible while it's
    // open): a coordinator who hits a 403/400 on save needs to see why
    // without closing the modal first.
    const [configureError, setConfigureError] = useState(null);
    // Page-level banner shown after the modal closes, so a saved edit is
    // confirmed even though the modal is gone.
    const [configureSuccessMessage, setConfigureSuccessMessage] =
        useState(null);

    // Identifies the filter combination a response belongs to. A response to
    // a filter the reader has already changed would otherwise overwrite, or
    // append to, the list they are now looking at.
    const requestIdRef = useRef(0);
    // Whether the first page for the current filters is still on its way.
    // A ref rather than the `loading` state for the same reason the
    // load-more latch is one: state is a render behind, so an intersection
    // callback firing in the gap between a filter change and the re-render
    // reads the old value, asks for the next page of the list being replaced,
    // and appends results for filters nobody is looking at any more.
    const firstPageInFlightRef = useRef(false);

    useEffect(() => {
        if (!currentTermCode) return undefined;
        let cancelled = false;
        courseService
            .getSubjectPrefixes(currentTermCode)
            .then((prefixes) => {
                if (!cancelled) setSubjectPrefixes(prefixes || []);
            })
            .catch(() => {
                // Losing this costs the department filter its options, not the
                // page: the catalog still lists and search still narrows it,
                // so it does not belong in the error banner.
                if (!cancelled) setSubjectPrefixes([]);
            });
        return () => {
            cancelled = true;
        };
    }, [currentTermCode]);

    const subjectParam =
        departmentFilter === ALL_DEPARTMENTS ? undefined : departmentFilter;

    // The first page, and every later change of term or filter.
    useEffect(() => {
        if (!currentTermCode) return undefined;
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        firstPageInFlightRef.current = true;
        setLoading(true);
        setError(null);
        setLoadMoreError(null);

        courseService
            .getCoursesByTerm(currentTermCode, {
                offset: 0,
                subject: subjectParam,
                q: searchQuery,
            })
            .then((data) => {
                if (requestIdRef.current !== requestId) return;
                setCourses(data.courses || []);
                setTotal(data.total || 0);
            })
            .catch(() => {
                if (requestIdRef.current !== requestId) return;
                setCourses([]);
                setTotal(0);
                setError('Failed to load courses. Please try again.');
            })
            .finally(() => {
                // Only the newest request clears the latch. An older one
                // settling later would otherwise reopen it while the current
                // first page is still outstanding.
                if (requestIdRef.current === requestId) {
                    firstPageInFlightRef.current = false;
                    setLoading(false);
                }
            });
        return undefined;
    }, [currentTermCode, subjectParam, searchQuery, firstPageAttempt]);

    const hasMore = courses.length < total;

    // Whether a page is already on its way. This is a ref and not the
    // `loadingMore` state because state is not visible until the next render:
    // two calls in the same tick, which is what an impatient second click or
    // a second intersection callback produces, would both read `false` and
    // both fetch the same offset.
    const inFlightRef = useRef(false);

    const loadMore = useCallback(async () => {
        if (inFlightRef.current || firstPageInFlightRef.current) return;
        if (!hasMore || !currentTermCode) return;
        const requestId = requestIdRef.current;
        inFlightRef.current = true;
        setLoadingMore(true);
        setLoadMoreError(null);
        try {
            const data = await courseService.getCoursesByTerm(currentTermCode, {
                offset: courses.length,
                subject: subjectParam,
                q: searchQuery,
            });
            // A filter changed while this page was in flight, so it belongs to
            // a list that no longer exists. Appending would mix two result
            // sets into one grid.
            if (requestIdRef.current !== requestId) return;
            setCourses((prev) => {
                // Offset paging can hand back a course already on screen: a
                // page requested against a stale count overlaps the previous
                // one, and so does any insert into the catalog between two
                // pages. A duplicate card is visible, and it also makes the
                // "showing N of M" count claim more courses than are listed.
                const seen = new Set(prev.map((course) => course._id));
                const added = (data.courses || []).filter(
                    (course) => !seen.has(course._id)
                );
                return added.length ? [...prev, ...added] : prev;
            });
            setTotal(data.total || 0);
        } catch (_err) {
            if (requestIdRef.current === requestId) {
                setLoadMoreError(
                    'Could not load more courses. The ones below are still here.'
                );
            }
        } finally {
            inFlightRef.current = false;
            setLoadingMore(false);
        }
    }, [hasMore, currentTermCode, courses.length, subjectParam, searchQuery]);

    // Watches the Load more button rather than a bare marker div, so the same
    // element serves a reader who scrolls and one who tabs to it. Where
    // IntersectionObserver is unavailable the button is still there and still
    // works; only the automatic part is lost.
    const loadMoreRef = useRef(null);
    useEffect(() => {
        const node = loadMoreRef.current;
        if (!node || !hasMore) return undefined;
        if (typeof IntersectionObserver !== 'function') return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) loadMore();
            },
            { rootMargin: INFINITE_SCROLL_MARGIN }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loadMore]);

    const getProgramBadge = (program) => {
        const config = {
            si: { bg: 'success', label: 'SI' },
            ta: { bg: 'primary', label: 'TA' },
            la: { bg: '', label: 'LA', className: 'badge-la' },
        };
        const programConfig = config[program];
        if (!programConfig) return null;
        const { bg, label, className = '' } = programConfig;
        return (
            <Badge
                key={program}
                bg={bg}
                text={badgeTextFor(bg)}
                className={`me-1 ${className}`.trim()}
            >
                {label}
            </Badge>
        );
    };

    // What this viewer can do with a course, shared by the card and the
    // details pop-out so the two can never offer different actions. Buttons
    // are small on the card and full size in the pop-out's footer.
    const courseActions = (course, { compact = true } = {}) => {
        const size = compact ? 'sm' : undefined;
        return (
            <>
                {/* Faculty and admin start a request; the chosen course is
                carried into step 1 of the form rather than edited here. */}
                {isFaculty && (
                    <Button
                        as={Link}
                        to={`/applications/new?course=${course._id}`}
                        state={cameFromState(location)}
                        variant="primary"
                        size={size}
                    >
                        Request Support
                    </Button>
                )}
                {/* Coordinators set which programs a course is eligible for, a
                property of the course unlike a section's details. Opening it closes the details pop-out, since two dialogs cannot
                share the focus. */}
                {canConfigure && (
                    <Button
                        variant="outline-primary"
                        size={size}
                        onClick={() => {
                            if (!compact) {
                                configureReturnFocusRef.current =
                                    document.getElementById(
                                        `course-title-${course._id}`
                                    );
                            }
                            setDetailCourse(null);
                            handleOpenConfigure(course);
                        }}
                    >
                        Configure
                    </Button>
                )}
                {!isFaculty &&
                    !coordinatorRole &&
                    applyEnabled &&
                    !hiringEnabled && (
                        <Button
                            as={Link}
                            to={`/apply?course=${course._id}`}
                            variant="outline-primary"
                            size={size}
                        >
                            Apply
                        </Button>
                    )}
                {readsPositions &&
                    openPositionKeys.has(
                        positionKey(course.subject, course.courseNumber)
                    ) && (
                        <Button
                            as={Link}
                            to={`/positions?${new URLSearchParams({
                                subject: course.subject,
                                courseNumber: course.courseNumber,
                            })}`}
                            variant="outline-primary"
                            size={size}
                        >
                            Apply
                            <span className="visually-hidden">
                                {' '}
                                to positions in {course.subject}{' '}
                                {course.courseNumber}
                            </span>
                        </Button>
                    )}
            </>
        );
    };

    const eligibilityBadges = (course) =>
        !hidesEligibility && course.eligiblePrograms?.length > 0
            ? course.eligiblePrograms.map(getProgramBadge)
            : null;

    // A click anywhere on a card opens its details, except on the card's own
    // links and buttons, which keep doing what they say. Keyboard and screen
    // reader users reach the same pop-out through the title, which is a real
    // button.
    const handleCardClick = (event, course) => {
        if (event.target.closest('a, button')) return;
        // Selecting text to copy it is not a request to open anything.
        if (window.getSelection?.()?.toString()) return;
        setDetailCourse(course);
    };

    const handleOpenConfigure = (course) => {
        setConfiguringCourse(course);
        setConfigureForm({
            eligible:
                course.eligiblePrograms?.includes(coordinatorProgram) ?? false,
        });
        setConfigureError(null);
        setConfigureSuccessMessage(null);
        setShowConfigureModal(true);
    };

    const handleSubmitConfigure = async (e) => {
        e.preventDefault();
        if (!configuringCourse) return;
        try {
            setSubmittingConfig(true);
            setConfigureError(null);
            setConfigureSuccessMessage(null);

            // Only our own program is ever sent. The server preserves any
            // other program's entry already on this course.
            const updated = await courseService.updateCourse(
                configuringCourse._id,
                {
                    eligiblePrograms: configureForm.eligible
                        ? [coordinatorProgram]
                        : [],
                }
            );

            setShowConfigureModal(false);
            setConfiguringCourse(null);
            setCourses((prev) =>
                prev.map((c) => (c._id === updated._id ? updated : c))
            );
            setConfigureSuccessMessage('Saved.');
        } catch (err) {
            const message =
                err.response?.data?.message ||
                'Failed to update course. Please try again.';
            setConfigureError(message);
        } finally {
            setSubmittingConfig(false);
        }
    };

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h1>Browse Courses</h1>
                    <p className="text-muted">
                        {/* isFaculty checked first, matching the card
                            button ternary below — a dual-role account
                            (faculty/admin + coordinator) can Request
                            Support here, so the header shouldn't read as
                            coordinator-only above a grid that offers them
                            exactly that. */}
                        {isFaculty
                            ? 'Find your course and request peer support'
                            : coordinatorRole
                              ? `Browse the full catalog and set which courses are eligible for ${COORDINATOR_PROGRAM_NAMES[coordinatorRole]}`
                              : applyEnabled
                                ? 'Find courses seeking peer support and apply to become a peer supporter'
                                : 'Browse the courses offered this term'}
                    </p>
                </div>
            </div>

            {configureSuccessMessage && (
                <Alert
                    variant="success"
                    dismissible
                    onClose={() => setConfigureSuccessMessage(null)}
                >
                    {configureSuccessMessage}
                </Alert>
            )}

            {isFaculty && (
                <ApplicationDeadlineCard
                    term={currentTerm}
                    facultyEmail={userProfile?.email}
                />
            )}

            {/* One search box, not two. Faculty are told the first thing to
                do right where they do it: find the course you teach, then
                press Request Support on its card. A separate "name your
                course" box above the catalog searched the same thing a second
                way, and nothing said which one to use. No program filter: the
                screen no longer advertises which programs a course is
                seeking. */}
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body>
                    {isFaculty && (
                        <>
                            <h2 className="h5 mb-1">
                                Know your course? Start here
                            </h2>
                            <p className="text-muted small mb-3">
                                Search for the course you teach, then press
                                Request Support on it.
                            </p>
                        </>
                    )}
                    <Row className="g-3 align-items-end">
                        <Col md={8}>
                            {/* Suggestions off: the grid below is where the
                                results go, so a dropdown would show the same
                                courses twice. */}
                            <CourseTypeahead
                                key={searchBoxKey}
                                // After Clear, the emptied box takes focus so
                                // the next search can be typed at once. Not on
                                // first load: taking focus there would pull a
                                // screen reader past the page heading.
                                autoFocus={searchBoxKey > 0}
                                termCode={currentTermCode || ''}
                                showSuggestions={false}
                                onSearch={setSearchQuery}
                                label={
                                    isFaculty
                                        ? 'Which course do you teach?'
                                        : 'Search courses'
                                }
                                placeholder="Course code or title, such as CHE 1800"
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                aria-label="Filter by department"
                                value={departmentFilter}
                                onChange={(e) =>
                                    setDepartmentFilter(e.target.value)
                                }
                            >
                                <option value={ALL_DEPARTMENTS}>
                                    All Departments
                                </option>
                                {subjectPrefixes.map((dept) => (
                                    <option key={dept} value={dept}>
                                        {dept}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={1}>
                            <Button
                                variant="outline-primary"
                                className="w-100"
                                onClick={() => {
                                    setSearchQuery('');
                                    setDepartmentFilter(ALL_DEPARTMENTS);
                                    setSearchBoxKey((k) => k + 1);
                                }}
                            >
                                Clear
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Results Count. Counts what the filters match, which is not what
                has been loaded; the rest arrives as the reader scrolls. */}
            {!loading && !termsLoading && !error && (
                <p className="text-muted mb-3" aria-live="polite">
                    {total === 0
                        ? searchQuery || subjectParam
                            ? 'No courses match your search'
                            : 'No courses are listed for this term yet'
                        : `Showing ${courses.length} of ${total} course${total === 1 ? '' : 's'}`}
                </p>
            )}

            {/* Error State */}
            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span>{error}</span>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => setFirstPageAttempt((n) => n + 1)}
                        >
                            Try again
                        </Button>
                    </div>
                </Alert>
            )}

            {/* Loading State */}
            {(loading || termsLoading) && (
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-muted">Loading courses...</p>
                </div>
            )}

            {/* Course List */}
            {!loading && !termsLoading && !error && (
                <Row>
                    {courses.length === 0 ? (
                        <Col>
                            <Card className="text-center py-5 border-0 bg-light">
                                <Card.Body>
                                    <p className="text-muted mb-0">
                                        {searchQuery || subjectParam
                                            ? 'No courses found matching your filters. Try adjusting your search criteria.'
                                            : 'Courses appear here once the catalog for this term is loaded.'}
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                    ) : (
                        courses.map((course) => (
                            <Col
                                md={6}
                                lg={4}
                                key={course._id}
                                className="mb-4"
                            >
                                <Card
                                    className="h-100 border-0 shadow-sm course-card"
                                    onClick={(event) =>
                                        handleCardClick(event, course)
                                    }
                                >
                                    {/* The course, not a section. This view
                                        is every course that could be taught,
                                        so it shows what the catalog says about
                                        the course and nothing that varies by
                                        section: schedule, room and
                                        enrollment are properties of a section,
                                        and those are collected on the
                                        application, where section data can
                                        later come from the student system each
                                        term. */}
                                    <Card.Body className="d-flex flex-column">
                                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                            <Badge bg="secondary">
                                                {course.subject}{' '}
                                                {course.courseNumber}
                                            </Badge>
                                            {creditsText(course) && (
                                                <small className="text-muted">
                                                    {creditsText(course)}
                                                </small>
                                            )}
                                            {course.guaranteedTransfer && (
                                                <Badge
                                                    bg="light"
                                                    text="dark"
                                                    className="border"
                                                    title="Colorado guaranteed transfer course"
                                                >
                                                    {/* The title tooltip is
                                                        mouse-only and many
                                                        screen readers skip
                                                        it, so the meaning of
                                                        a bare "GT-SC1" is also
                                                        spoken. */}
                                                    <span className="visually-hidden">
                                                        Guaranteed
                                                        transfer:{' '}
                                                    </span>
                                                    {course.guaranteedTransfer}
                                                </Badge>
                                            )}
                                        </div>
                                        <Card.Title className="h6 mb-2">
                                            <button
                                                type="button"
                                                id={`course-title-${course._id}`}
                                                className="btn btn-link p-0 text-start text-reset course-card-title"
                                                onClick={() =>
                                                    setDetailCourse(course)
                                                }
                                            >
                                                <span>{course.title}</span>
                                                <span className="visually-hidden">
                                                    , show details
                                                </span>
                                            </button>
                                        </Card.Title>
                                        {course.generalStudies && (
                                            <p className="small text-muted mb-2">
                                                General studies:{' '}
                                                {course.generalStudies}
                                            </p>
                                        )}
                                        {course.description && (
                                            <p className="small mb-3 course-card-description">
                                                {course.description}
                                            </p>
                                        )}
                                        {eligibilityBadges(course) && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block mb-1">
                                                    Seeking:
                                                </small>
                                                {eligibilityBadges(course)}
                                            </div>
                                        )}
                                        <div className="d-flex justify-content-between align-items-center gap-2 mt-auto">
                                            {course.catalogUrl ? (
                                                <a
                                                    href={course.catalogUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="small"
                                                >
                                                    View in catalog
                                                    <span className="visually-hidden">
                                                        {' '}
                                                        for {
                                                            course.subject
                                                        }{' '}
                                                        {course.courseNumber}{' '}
                                                        (opens in a new tab)
                                                    </span>
                                                </a>
                                            ) : (
                                                <span />
                                            )}
                                            <div className="d-flex gap-2">
                                                {courseActions(course)}
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))
                    )}
                </Row>
            )}

            {/* Infinite scroll. The button is the real control and works on
                its own; the observer above just presses it early for a reader
                who is scrolling. */}
            {loadMoreError && (
                <Alert
                    variant="warning"
                    dismissible
                    onClose={() => setLoadMoreError(null)}
                    className="mt-3"
                >
                    {loadMoreError}
                </Alert>
            )}

            {!loading && !termsLoading && !error && hasMore && (
                <div className="text-center my-4">
                    <Button
                        ref={loadMoreRef}
                        variant="outline-primary"
                        onClick={loadMore}
                        disabled={loadingMore}
                    >
                        {loadingMore ? (
                            <>
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    className="me-2"
                                >
                                    <span className="visually-hidden">
                                        Loading more courses
                                    </span>
                                </Spinner>
                                Loading more courses...
                            </>
                        ) : (
                            `${loadMoreError ? 'Try again' : 'Load more courses'} (${total - courses.length} remaining)`
                        )}
                    </Button>
                </div>
            )}

            {/* Info Card */}
            <Card className="mt-4 bg-light border-0">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={8}>
                            {/* isFaculty checked first, matching the card
                                button ternary and the page header above —
                                a dual-role account gets the faculty copy,
                                since the course grid actually offers them
                                Request Support, not just Configure.
                                Level structural, h5 class keeps the size. */}
                            <h2 className="h5 fw-bold mb-2">
                                {isFaculty
                                    ? 'Requesting Course Support'
                                    : coordinatorRole
                                      ? `${COORDINATOR_PROGRAM_NAMES[coordinatorRole]} Program`
                                      : 'Applying as a Peer Supporter'}
                            </h2>
                            {isFaculty || coordinatorRole || applyEnabled ? (
                                <p className="text-muted mb-0">
                                    {isFaculty
                                        ? "Submit a course support request to indicate which programs you'd like for your course. Rank your preferences: each program reviews independently on their own timeline."
                                        : coordinatorRole
                                          ? `Mark which courses are eligible for ${COORDINATOR_PROGRAM_NAMES[coordinatorRole]} using Configure.${coordinatorReviewEnabled ? ' Your program dashboard is where you review applications and manage assignments.' : ''}`
                                          : hiringEnabled
                                            ? 'Apply to open Learning Assistant positions. Open Positions lists the sections hiring now; you fill in your LA application once, then apply to positions.'
                                            : "Apply to support a course as an SI Leader or Learning Assistant. You'll provide your qualifications, select courses you've excelled in, and rank your program preferences."}
                                    {hiringEnabled &&
                                        !isFaculty &&
                                        !coordinatorRole && (
                                            <>
                                                {' '}
                                                <Link to="/positions">
                                                    See open positions
                                                </Link>
                                            </>
                                        )}
                                </p>
                            ) : (
                                <StudentApplicationsNotOpen className="text-muted" />
                            )}
                        </Col>
                        <Col md={4} className="text-md-end mt-3 mt-md-0">
                            <Button
                                as={Link}
                                to="/about#programs"
                                variant="link"
                            >
                                Learn more about programs &rarr;
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
            <CourseDetailModal
                course={detailCourse}
                onHide={() => setDetailCourse(null)}
                eligibility={detailCourse && eligibilityBadges(detailCourse)}
                actions={
                    detailCourse &&
                    courseActions(detailCourse, { compact: false })
                }
            />

            {/* Configure Course Modal */}
            <Modal
                aria-labelledby="configure-course-modal-title"
                show={showConfigureModal}
                onExited={() => {
                    const target = configureReturnFocusRef.current;
                    configureReturnFocusRef.current = null;
                    target?.focus();
                }}
                onHide={() => setShowConfigureModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="configure-course-modal-title">
                        Course Eligibility
                    </Modal.Title>
                </Modal.Header>
                <Form noValidate onSubmit={handleSubmitConfigure}>
                    <Modal.Body>
                        {configureError && (
                            <Alert
                                variant="danger"
                                dismissible
                                onClose={() => setConfigureError(null)}
                            >
                                {configureError}
                            </Alert>
                        )}
                        {configuringCourse && (
                            <Alert variant="light" className="border mb-3">
                                <strong>
                                    {configuringCourse.subject}{' '}
                                    {configuringCourse.courseNumber}:{' '}
                                    {configuringCourse.title}
                                </strong>
                            </Alert>
                        )}
                        <Form.Check
                            type="checkbox"
                            id="configureEligible"
                            label={`Eligible for ${COORDINATOR_PROGRAM_NAMES[coordinatorRole]}`}
                            checked={configureForm.eligible}
                            onChange={(e) =>
                                setConfigureForm((prev) => ({
                                    ...prev,
                                    eligible: e.target.checked,
                                }))
                            }
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline-primary"
                            onClick={() => setShowConfigureModal(false)}
                            disabled={submittingConfig}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={submittingConfig}
                        >
                            {submittingConfig ? 'Saving...' : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
}

export default CoursesPage;
